<template>
  <div class="voice-search-overlay" @click.self="handleClose">
    <div class="voice-search-sheet">
      <div class="sheet-header">
        <span class="sheet-title">语音识别</span>
        <span class="sheet-close" @click="handleClose">✕</span>
      </div>

      <!-- idle / recording 共用一个持久容器，保证 touchend 能路由到同一个 DOM 元素 -->
      <div
        v-if="status === 'idle' || status === 'recording'"
        class="sheet-body"
        @touchstart.prevent="handleTouchStart"
        @touchend.prevent="handleTouchEnd"
        @touchcancel.prevent="handleTouchCancel"
        @mousedown.prevent="handleTouchStart"
        @mouseup.prevent="handleTouchEnd"
      >
        <div class="mic-btn" :class="{ recording: status === 'recording' }">
          <div class="mic-icon" :style="{ backgroundImage: `url(${voiceStaticIcon})` }"></div>
          <template v-if="status === 'recording'">
            <span class="ripple ripple1"></span>
            <span class="ripple ripple2"></span>
            <span class="ripple ripple3"></span>
          </template>
        </div>
        <div v-if="status === 'idle'" class="hint-wrap">
          <p class="hint-text">长按按钮，直接说歌名或歌手名</p>
          <p class="hint-example">如：「后来」「刘若英」「告白气球」</p>
          <p class="hint-tip">无需说"帮我搜索"，直接说名字效果最佳</p>
        </div>
      </div>

      <!-- recognizing 状态 -->
      <div v-else-if="status === 'recognizing'" class="sheet-body recognizing-body">
        <p class="recognizing-text">正在聆听</p>
        <div class="dots">
          <span
            v-for="i in 7"
            :key="i"
            class="dot"
            :style="{ animationDelay: (i - 1) * 0.15 + 's' }"
          ></span>
        </div>
        <button class="stop-btn" @click="handleStop">停止</button>
      </div>

    </div>
  </div>
</template>

<script>
import weixin from '@/libs/app/weixin'

const VOICE_ICON = 'https://qncweb.ktvsky.com/20260423/vadd/61b8e9064322a35e0f864d775c447106.png'
const isDev = process.env.NODE_ENV === 'development'

// 常见指令前缀
const PREFIXES = [
  /^帮我点(播)?(一首)?/,
  /^帮我搜(索)?(一下)?/,
  /^帮我来(一首)?/,
  /^帮我找(一下)?/,
  /^搜(索)?(一下|下)?/,
  /^来(一首)?/,
  /^点(播|一首)?/,
  /^我想听(一首)?/,
  /^播放(一首)?/,
  /^找(一下)?/,
  /^给我(来)?(搜)?(一首)?/,
  /^唱(一首)?/,
]

// 常见语气尾缀
const SUFFIXES = [
  /好吗[？?]?$/, /可以吗[？?]?$/, /吧[？?]?$/, /[？?！!。，,、]+$/, /谢谢$/, /啊$/, /嘛$/, /呢$/,
]

// ASR 插入的所有标点（全角 + 半角）
const PUNCT_RE = /[，,。.？?！!、；;：:""''「」【】《》～~\s]+/g

function parseVoiceQuery(raw) {
  let text = raw.trim()
  PREFIXES.forEach((p) => { text = text.replace(p, '').trim() })
  SUFFIXES.forEach((p) => { text = text.replace(p, '').trim() })
  text = text.replace(PUNCT_RE, '').trim()
  return text
}

// dev 环境下 wx API 不可用，用 mock 模拟录音流程
const devWeixin = {
  startRecord(successCb) {
    setTimeout(() => successCb && successCb(), 50)
  },
  stopRecord(successCb) {
    if (successCb) {
      setTimeout(() => successCb('测试歌曲识别结果'), 1500)
    }
  },
}

const wx = isDev ? devWeixin : weixin

const isAndroid = /Android/i.test(navigator.userAgent)

export default {
  name: 'VoiceSearchDialog',
  data() {
    return {
      status: 'idle', // idle | recording | recognizing
      voiceStaticIcon: VOICE_ICON,
      // Android: SDK 用当前 URL 验签，每次弹窗打开时重新初始化
      sdkReady: !isAndroid || isDev,
    }
  },
  created() {
    this.isCancelled = false
    if (isAndroid && !isDev) {
      weixin.init(() => { this.sdkReady = true })
    }
  },
  methods: {
    handleClose() {
      if (this.status === 'recording' || this.status === 'recognizing') {
        this.isCancelled = true
        wx.stopRecord(null, null)
      }
      this.$store.commit('SET_SHOW_VOICE_SEARCH', false)
    },
    handleTouchStart() {
      if (this.status !== 'idle') return
      if (!this.sdkReady) return
      this.isCancelled = false
      this.$sendLog({
        event_type: 'click',
        event_name: '语音点歌',
        event_data: {
          action: '长按录音按钮点击',
        },
      })
      wx.startRecord(
        () => {
          if (this.isCancelled) {
            wx.stopRecord(null, null)
            return
          }
          this.status = 'recording'
        },
        () => {
          this.status = 'idle'
        }
      )
    },
    handleTouchCancel() {
      // 原生层弹窗（如微信授权框）劫持触摸时触发，touchend 不会到达 web 层
      this.isCancelled = true
      if (this.status === 'recording') {
        wx.stopRecord(null, null)
      }
      this.status = 'idle'
    },
    handleTouchEnd() {
      if (this.status === 'idle') {
        // 手指抬起时录音还未开始（极短按），标记取消
        this.isCancelled = true
        return
      }
      if (this.status !== 'recording') return
      this.status = 'recognizing'
      wx.stopRecord(
        (text) => {
          if (this.isCancelled) return
          const query = parseVoiceQuery(text)
          if (!query) {
            this.status = 'idle'
            return
          }
          sessionStorage.setItem('voiceSearchRedirect', '1')
          this.$router.push(`/thunder/search?songname=${encodeURIComponent(query)}&time=${Date.now()}`)
          this.$store.commit('SET_SHOW_VOICE_SEARCH', false)
        },
        () => {
          if (this.isCancelled) return
          this.status = 'idle'
        }
      )
    },
    handleStop() {
      wx.stopRecord(null, () => {})
      this.status = 'idle'
    },
  },
  beforeDestroy() {
    if (this.status === 'recording') {
      wx.stopRecord(null, null)
    }
  },
}
</script>

<style lang="scss" scoped>
.voice-search-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: $z_index_important - 1;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.voice-search-sheet {
  width: 690px;
  height: 720px;
  background: #3B8CFF;
  border-radius: 60px;
  padding: 40px 40px 60px;
  margin-bottom: 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.sheet-header {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  margin-bottom:110px;
}

.sheet-title {
  font-size: 32px;
  font-weight: 600;
  color: #fff;
}

.sheet-close {
  position: absolute;
  right: 0;
  font-size: 32px;
  color: rgba(255, 255, 255, 0.8);
  padding: 10px;
}

.sheet-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

/* 麦克风按钮 */
.mic-btn {
  width: 198px;
  height: 198px;
  border-radius: 50%;
  background: linear-gradient(180deg, #FFFFFF 0%, #9DDDFF 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-bottom: 30px;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
}

.mic-icon {
  width: 80px;
  height: 80px;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  pointer-events: none;
}

.hint-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.hint-text {
  font-size: 28px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  text-align: center;
}

.hint-example {
  font-size: 26px;
  color: rgba(255, 255, 255, 0.75);
  text-align: center;
}

.hint-tip {
  font-size: 22px;
  color: rgba(255, 255, 255, 0.5);
  text-align: center;
}

/* 水波纹动画 */
.ripple {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  animation: ripple-expand 1.5s ease-out infinite;
}
.ripple1 { width: 198px; height: 198px; animation-delay: 0s; }
.ripple2 { width: 198px; height: 198px; animation-delay: 0.5s; }
.ripple3 { width: 198px; height: 198px; animation-delay: 1s; }

@keyframes ripple-expand {
  0%   { transform: scale(1);    opacity: 0.6; }
  100% { transform: scale(2.32); opacity: 0; }
}

/* recognizing */
.recognizing-body {
  min-height: 300px;
  justify-content: center;
}

.recognizing-text {
  font-size: 36px;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 40px;
}

.dots {
  display: flex;
  gap: 16px;
  margin-bottom: 60px;
}

.dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  animation: dot-blink 1.05s ease-in-out infinite both;
  opacity: 0;
}

@keyframes dot-blink {
  0%, 80%, 100% { opacity: 0; transform: scale(0.8); }
  40%            { opacity: 1; transform: scale(1); }
}

.stop-btn {
  width: 200px;
  height: 80px;
  border-radius: 40px;
  border: 2px solid rgba(255, 255, 255, 0.6);
  background: transparent;
  color: #fff;
  font-size: 30px;
}

</style>
